import React from 'react';
import {
  Container,
  Paper,
  Title,
  Text,
  Group,
  Grid,
  Card,
  Stack,
  Table,
  Badge,
  Loader,
  Alert,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useContractDashboardStats } from '../hooks/useContracts';
import { useContracts } from '../hooks/useContracts';
import dayjs from 'dayjs';

export function ContractDashboard() {
  const navigate = useNavigate();
  const { data: stats, isLoading: statsLoading } = useContractDashboardStats();
  const { data: contracts, isLoading: contractsLoading } = useContracts({ expiringSoon: true });

  if (statsLoading || contractsLoading) {
    return (
      <Container size="xl" py="xl">
        <Loader />
      </Container>
    );
  }

  const expiringContracts = contracts?.filter((c) => {
    if (!c.endDate) return false;
    const daysUntilExpiry = dayjs(c.endDate).diff(dayjs(), 'day');
    return daysUntilExpiry <= 90 && daysUntilExpiry >= 0;
  }) || [];

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" mb="xl">
        <Title order={2}>Contract Dashboard</Title>
        <Group>
          <Text size="sm" c="dimmed">
            Last updated: {dayjs().format('DD MMM YYYY HH:mm')}
          </Text>
        </Group>
      </Group>

      {/* Stats Cards */}
      <Grid mb="xl">
        <Grid.Col span={3}>
          <Card shadow="sm" p="md" withBorder>
            <Stack gap="xs">
              <Text size="sm" c="dimmed">
                Total Contracts
              </Text>
              <Text fw={700} size="xl">
                {stats?.totalContracts || 0}
              </Text>
            </Stack>
          </Card>
        </Grid.Col>
        <Grid.Col span={3}>
          <Card shadow="sm" p="md" withBorder>
            <Stack gap="xs">
              <Text size="sm" c="dimmed">
                Active Contracts
              </Text>
              <Text fw={700} size="xl" c="green">
                {stats?.activeContracts || 0}
              </Text>
            </Stack>
          </Card>
        </Grid.Col>
        <Grid.Col span={3}>
          <Card shadow="sm" p="md" withBorder>
            <Stack gap="xs">
              <Text size="sm" c="dimmed">
                Expired Contracts
              </Text>
              <Text fw={700} size="xl" c="red">
                {stats?.expiredContracts || 0}
              </Text>
            </Stack>
          </Card>
        </Grid.Col>
        <Grid.Col span={3}>
          <Card shadow="sm" p="md" withBorder>
            <Stack gap="xs">
              <Text size="sm" c="dimmed">
                Expiring Soon
              </Text>
              <Text fw={700} size="xl" c="orange">
                {stats?.upcomingContracts || 0}
              </Text>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      {/* Expiring Contracts */}
      <Paper shadow="sm" p="md">
        <Title order={4} mb="md">
          Contracts Expiring Soon (Next 3 Months)
        </Title>
        {expiringContracts.length > 0 ? (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Contract Number</Table.Th>
                <Table.Th>Supplier</Table.Th>
                <Table.Th>End Date</Table.Th>
                <Table.Th>Days Remaining</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {expiringContracts.map((contract) => {
                const daysRemaining = contract.endDate
                  ? dayjs(contract.endDate).diff(dayjs(), 'day')
                  : 0;
                return (
                  <Table.Tr
                    key={contract.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/contracts/${contract.id}`)}
                  >
                    <Table.Td>{contract.contractNumber}</Table.Td>
                    <Table.Td>{contract.supplierName}</Table.Td>
                    <Table.Td>
                      {contract.endDate ? dayjs(contract.endDate).format('DD MMM YYYY') : '—'}
                    </Table.Td>
                    <Table.Td>
                      <Badge color={daysRemaining <= 30 ? 'red' : 'orange'}>
                        {daysRemaining} days
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge color="green">{contract.status}</Badge>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        ) : (
          <Alert icon={<IconAlertCircle size={16} />} color="blue">
            No contracts expiring in the next 3 months
          </Alert>
        )}
      </Paper>
    </Container>
  );
}

